import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import ModalDrawerSwitcher from '../ModalDrawerSwitcher';
import ServiceOrderUpdate from './ActivitiesRegistrationSources/ServiceOrder/ServiceOrderUpdate';
import ServiceOrderRemotion from './ActivitiesRegistrationSources/ServiceOrder/ServiceOrderRemotion';
import InvoiceCancelling from './ActivitiesRegistrationSources/Invoice/InvoiceCancelling';
import InvoiceUpdate from './ActivitiesRegistrationSources/Invoice/InvoiceUpdate';
import InvoiceRestoration from './ActivitiesRegistrationSources/Invoice/InvoiceRestoration';
import InvoiceUndoPayment from './ActivitiesRegistrationSources/Invoice/InvoiceUndoPayment';
import InvoiceClosing from './ActivitiesRegistrationSources/Invoice/InvoiceClosing';
import TransactionUpdate from './ActivitiesRegistrationSources/Transaction/TransactionUpdate';
import TransactionRemotion from './ActivitiesRegistrationSources/Transaction/TransactionRemotion';
import PayrollUpdate from './ActivitiesRegistrationSources/Payroll/PayrollUpdate';
import PayrollUndoPayment from './ActivitiesRegistrationSources/Payroll/PayrollUndoPayment';
import PayrollSheetUpdate from './ActivitiesRegistrationSources/PayrollSheet/PayrollSheetUpdate';
import PayrollSheetUndoPayment from './ActivitiesRegistrationSources/PayrollSheet/PayrollSheetUndoPayment';
import ConfigurationUpdate from './ActivitiesRegistrationSources/Configuration/ConfigurationUpdate';
import PasswordUpdate from './ActivitiesRegistrationSources/Password/PasswordUpdate';
import SellOrderRemotion from './ActivitiesRegistrationSources/SellOrder/SellOrderRemotion';
import SchedulingUpdate from './ActivitiesRegistrationSources/Scheduling/SchedulingUpdate';
import SchedulingRemotion from './ActivitiesRegistrationSources/Scheduling/SchedulingRemotion';
import SchedulingConfiguration from './ActivitiesRegistrationSources/Scheduling/SchedulingConfiguration';
import RemoveOS from './ActivitiesRegistrationSources/AdvancedConfigurations/RemoveOS';
import VehicleExit from './ActivitiesRegistrationSources/AdvancedConfigurations/VehicleExit';
import ChangeAccount from './ActivitiesRegistrationSources/AdvancedConfigurations/ChangeAccount';
import CertificateUpload from './ActivitiesRegistrationSources/ProvisionalReceipt/CertificateUpload';
import SendNfse from './ActivitiesRegistrationSources/ProvisionalReceipt/SendNfse';
import NfseUpdate from './ActivitiesRegistrationSources/ProvisionalReceipt/NfseUpdate';
import NfeUpdate from './ActivitiesRegistrationSources/ProvisionalReceipt/NfeUpdate';
import ClientUpdate from './ActivitiesRegistrationSources/Client/ClientUpdate';
import ClientStatus from './ActivitiesRegistrationSources/Client/ClientStatus';
import SessionClose from './ActivitiesRegistrationSources/Session/SessionClose';
import DebitWarningActivation from './ActivitiesRegistrationSources/DebitWarning/DebitWarningActivation';
import DebitWarningDeactivation from './ActivitiesRegistrationSources/DebitWarning/DebitWarningDeactivation';
import { Activation as EstablishmentActivation, Deactivation as EstablishmentDeactivation, Update as EstablishmentUpdate } from './ActivitiesRegistrationSources/Establishment';
import UserUpdate from './ActivitiesRegistrationSources/User/UserUpdate';
import PaymentMethodUpdate from './ActivitiesRegistrationSources/PaymentMethod/PaymentMethodUpdate';
import { Activation as SelfServiceActivation, Deactivation as SelfServiceDeactivation } from './ActivitiesRegistrationSources/SelfService';

import { loadServiceOrder } from 'components/ServiceOrder/store/thunk';
import { loadInvoiceDetails } from 'pages/ClientInvoices/store/thunk';
import { loadSingleTransaction } from 'pages/Transactions/store/thunk';
import { loadSinglePayroll } from 'pages/ComissionedPayrolls/store/thunk';
import { loadSinglePayrollSheet } from 'pages/ComissionedPayrollSheet/store/thunk';
import { loadConfigurations } from 'pages/Operational/store/thunk';
import { loadEmployeeRegister } from 'pages/EmployeesList/store/thunk';

import { setTransactionDetails } from 'pages/Transactions/store/actions';

import { REFACTOR_DATE } from './Resources';

export default function ActivitiesRegistrationDetails({
  title,
  isOpenActivitiesRegistrationDetails,
  setIsOpenActivitiesRegistrationDetails,
  data
}) {
  const dispatch = useDispatch();

  const {
    establishmentId,
    activitySourceId,
    activityTypeId,
    registerId,
    lastRegister,
    newRegister,
    userName,
    description,
    observation,
    createdAt,
    updatedAt
  } = data || {};

  const { userId } = useSelector(state => state.profile);
  const serviceOrder = useSelector(state => state.serviceOrder);
  const invoice = useSelector(state => state.clientInvoices.submit);
  const transaction = useSelector(state => state.transactions.submit);
  const payroll = useSelector(state => state.comissionedPayrolls.submit);
  const payrollSheet = useSelector(state => state.comissionedPayrollSheet.submit);
  const { name } = useSelector(state => state.employeesList.employeeRegister);
  const { cashier, operation, receipt, vehicleEntry } = useSelector(state => state.operational);

  const isOsUpdate = +activitySourceId == 1 && +activityTypeId == 1;
  const isOsRemotion = +activitySourceId == 1 && +activityTypeId == 2;
  const isInvoiceUpdate = +activitySourceId == 2 && +activityTypeId == 1;
  const isInvoiceCancelling = +activitySourceId == 2 && +activityTypeId == 3;
  const isInvoiceRestoration = +activitySourceId == 2 && +activityTypeId == 4;
  const isInvoiceUndoPayment = +activitySourceId == 2 && +activityTypeId == 5;
  const isInvoiceClosing = +activitySourceId == 2 && +activityTypeId == 7;
  const isTransactionUpdate = +activitySourceId == 3 && +activityTypeId == 1;
  const isTransactionRemotion = +activitySourceId == 3 && +activityTypeId == 2;
  const isPayrollUpdate = +activitySourceId == 7 && +activityTypeId == 1;
  const isPayrollUndoPayment = +activitySourceId == 7 && +activityTypeId == 5;
  const isPayrollSheetUpdate = +activitySourceId == 6 && +activityTypeId == 1;
  const isPayrollSheetUndoPayment = +activitySourceId == 6 && +activityTypeId == 5;
  const isConfigurationUpdate = +activitySourceId == 8 && +activityTypeId == 1;
  const isEstablishmentUpdate = +activitySourceId == 21 && +activityTypeId == 1;
  const isPasswordUpdate = +activitySourceId == 9 && +activityTypeId == 1;
  const isSellOrderRemotion = +activitySourceId == 11 && +activityTypeId == 2;
  const isSchedulingUpdate = +activitySourceId == 13 && +activityTypeId == 1;
  const isSchedulingRemotion = +activitySourceId == 13 && +activityTypeId == 2;
  const isSchedulingConfiguration = +activitySourceId == 14 && +activityTypeId == 1;
  const isAdvancedConfigRemoveOS = +activitySourceId == 16 && +activityTypeId == 2;
  const isAdvancedConfigVehicleExit = +activitySourceId == 15 && +activityTypeId == 6;
  const isAdvancedConfigChangeAccount = +activitySourceId == 17 && +activityTypeId == 1;
  const isSendNfse = +activitySourceId == 20 && +activityTypeId == 9;
  const isNfseUpdate = +activitySourceId == 20 && +activityTypeId == 1;
  const isNfeUpdate = +activitySourceId == 29 && +activityTypeId == 1;
  const isSessionClose = +activitySourceId == 23 && +activityTypeId == 7;
  const isClientUpdate = +activitySourceId == 4 && +activityTypeId == 1;
  const isClientStatus = (+activitySourceId == 4 && +activityTypeId == 8) || (+activitySourceId == 4 && +activityTypeId == 9);
  const isUserUpdate = +activitySourceId == 22 && +activityTypeId == 1;
  const isPaymentMethodUpdate = +activitySourceId == 30 && +activityTypeId == 1;
  const isSelfServiceActivation = +activitySourceId == 34 && +activityTypeId == 8;
  const isSelfServiceDeactivation = +activitySourceId == 34 && +activityTypeId == 9;

  const isCertificateUpload = newRegister?.certificateUpload == 'completed';
  const isDebitWarningActivation = +newRegister?.debitWarning == 1;
  const isDebitWarningDeactivation = +newRegister?.debitWarning == 0;
  const isEstablishmentActivation = +newRegister?.appPermission == 1;
  const isEstablishmentDeactivation = +newRegister?.appPermission == 0;

  const getActivityDataDescription = () => {
    if(isInvoiceCancelling || isInvoiceUndoPayment || isTransactionRemotion || isOsUpdate || isOsRemotion || isSellOrderRemotion || isAdvancedConfigRemoveOS || isAdvancedConfigVehicleExit || isClientUpdate) {
      return observation;
    }

    return description;
  }

  const activityData = {
    userName,
    createdAt,
    updatedAt,
    description: getActivityDataDescription()
  }

  useEffect(() => {
    if(moment(updatedAt, 'YYYY-MM-DD HH:mm:ss').isBefore(REFACTOR_DATE)) {
      (async () => {
        if(isOsUpdate) {
          const accountProps = {
            serviceOrderId: registerId,
            userId,
            establishmentId
          }

          dispatch(loadServiceOrder(accountProps));
        }

        if(isInvoiceUpdate) {
          const {
            establishmentId,
            clientId,
            invoiceDateTime
          } = lastRegister?.invoice;

          dispatch(loadInvoiceDetails(userId, establishmentId, clientId, invoiceDateTime));
        }

        if(isTransactionUpdate) {
          const {
            establishmentId,
            transactionId
          } = lastRegister?.transaction;

          const data = await dispatch(loadSingleTransaction(userId, establishmentId, transactionId));

          if(data) {
            await dispatch(setTransactionDetails(data));
          }
        }

        if(isPayrollUpdate) {
          dispatch(loadSinglePayroll(userId, establishmentId, registerId));
        }

        if(isPayrollSheetUpdate) {
          dispatch(loadSinglePayrollSheet(userId, establishmentId, registerId));
        }

        if(isConfigurationUpdate) {
          dispatch(loadConfigurations(userId, establishmentId));
        }

        if(isPasswordUpdate) {
          dispatch(loadEmployeeRegister(userId, registerId));
        }
      })();
    }
  }, []);

  return(
    <ModalDrawerSwitcher
      id="activitiesRegistrationDetails"
      title={title}
      open={isOpenActivitiesRegistrationDetails}
      onClose={() => setIsOpenActivitiesRegistrationDetails(false)}
    >
      {isOsUpdate && (
        <ServiceOrderUpdate
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
          serviceOrder={serviceOrder}
        />
      )}
      {isOsRemotion && (
        <ServiceOrderRemotion
          lastRegister={lastRegister?.serviceOrder}
          activityData={activityData}
        />
      )}
      {isInvoiceCancelling && (
        <InvoiceCancelling
          lastRegister={lastRegister}
          activityData={activityData}
        />
      )}
      {isInvoiceUpdate && (
        <InvoiceUpdate
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
          invoice={invoice}
        />
      )}
      {isInvoiceRestoration && (
        <InvoiceRestoration
          activityData={activityData}
          newRegister={newRegister}
        />
      )}
      {isInvoiceUndoPayment && (
        <InvoiceUndoPayment
          lastRegister={lastRegister}
          activityData={activityData}
        />
      )}
      {isInvoiceClosing && (
        <InvoiceClosing data={{ description }} />
      )}
      {isTransactionUpdate && (
        <TransactionUpdate
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
          transaction={transaction}
        />
      )}
      {isTransactionRemotion && (
        <TransactionRemotion
          lastRegister={lastRegister}
          activityData={activityData}
        />
      )}
      {isPayrollUpdate && (
        <PayrollUpdate
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
          payroll={payroll}
        />
      )}
      {isPayrollUndoPayment && (
        <PayrollUndoPayment
          lastRegister={lastRegister}
          activityData={activityData}
        />
      )}
      {isPayrollSheetUpdate && (
        <PayrollSheetUpdate
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
          payrollSheet={payrollSheet}
        />
      )}
      {isPayrollSheetUndoPayment && (
        <PayrollSheetUndoPayment
          lastRegister={lastRegister}
          activityData={activityData}
        />
      )}
      {isConfigurationUpdate && (
        <ConfigurationUpdate
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
          configuration={{ ...cashier, ...operation, ...receipt, ...vehicleEntry }}
        />
      )}
      {isEstablishmentUpdate && (
        <EstablishmentUpdate
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
        />
      )}
      {isPasswordUpdate && (
        <PasswordUpdate
          data={{ description, userName, name, createdAt }}
        />
      )}
      {isSellOrderRemotion && (
        <SellOrderRemotion
          lastRegister={JSON.parse(lastRegister[0])}
          activityData={activityData}
        />
      )}
      {isSchedulingUpdate && (
        <SchedulingUpdate
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
        />
      )}
      {isSchedulingRemotion && (
        <SchedulingRemotion
          activityData={activityData}
          lastRegister={Array.isArray(lastRegister) ? JSON.parse(lastRegister[0]) : lastRegister}
        />
      )}
      {isSchedulingConfiguration && (
        <SchedulingConfiguration
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
        />
      )}
      {isAdvancedConfigRemoveOS && (
        <RemoveOS
          lastRegister={lastRegister}
          activityData={activityData}
        />
      )}
      {isAdvancedConfigVehicleExit && (
        <VehicleExit
          lastRegister={lastRegister}
          activityData={activityData}
        />
      )}
      {isAdvancedConfigChangeAccount && (
        <ChangeAccount
          lastRegister={lastRegister}
          activityData={activityData}
        />
      )}
      {isCertificateUpload && (
        <CertificateUpload data={{ userName, createdAt }} />
      )}
      {isSendNfse && (
        <SendNfse data={{ userName, createdAt }} />
      )}
      {isNfeUpdate && (
        <NfeUpdate
          data={{ establishmentId, observation, createdAt }}
          lastRegister={lastRegister}
          newRegister={newRegister}
        />
      )}
      {isNfseUpdate && (
        <NfseUpdate
          data={{ establishmentId, observation, createdAt }}
          lastRegister={lastRegister}
          newRegister={newRegister}
        />
      )}
      {isSessionClose && (
        <SessionClose data={{ userName, createdAt }} />
      )}
      {isClientUpdate && (
        <ClientUpdate
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
        />
      )}
      {isClientStatus && (
        <ClientStatus data={{ observation, createdAt }} />
      )}
      {isUserUpdate && (
        <UserUpdate
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
        />
      )}
      {isPaymentMethodUpdate && (
        <PaymentMethodUpdate
          activityData={activityData}
          lastRegister={lastRegister}
          newRegister={newRegister}
        />
      )}
      {isDebitWarningActivation && (
        <DebitWarningActivation data={{ establishmentId, createdAt, userName }} />
      )}
      {isDebitWarningDeactivation && (
        <DebitWarningDeactivation data={{ establishmentId, createdAt, userName }} />
      )}
      {isEstablishmentActivation && (
        <EstablishmentActivation data={{ establishmentId, createdAt, userName }} />
      )}
      {isEstablishmentDeactivation && (
        <EstablishmentDeactivation data={{ establishmentId, createdAt, userName }} />
      )}
      {isSelfServiceActivation && (
        <SelfServiceActivation data={{ userName, createdAt }} />
      )}
      {isSelfServiceDeactivation && (
        <SelfServiceDeactivation data={{ userName, createdAt }} />
      )}
    </ModalDrawerSwitcher>
  );
}

ActivitiesRegistrationDetails.propTypes = {
  title: PropTypes.string.isRequired,
  isOpenActivitiesRegistrationDetails: PropTypes.bool.isRequired,
  setIsOpenActivitiesRegistrationDetails: PropTypes.func.isRequired,
  data: PropTypes.any.isRequired
};